import { MAX_FILE_SIZE } from 'appConstants';
import { AttachmentItems } from 'reducers';
import { MiscUtils } from 'services';
import {
  Attachment,
  AttachmentAWS,
  AttachmentAWSData,
  AttachmentData,
  AttachmentResponse,
  FileInfo,
} from 'types';
import fileIconMap from './fileIconMap.json';

enum AttachmentErrors {
  ATTACHMENT_DATA_FAILURE,
  INVALID_EXTENSION,
  INVALID_NAME_LENGTH,
  INVALID_SIZE,
  UPLOAD_FAILURE,
}

const AttachmentUtils = {
  /** Takes an AttachmentItems object and returns values as an array */
  asArray: (attachments: AttachmentItems) =>
    Object.keys(attachments).map((id) => attachments[id]),

  /** Composes an Attachment with FileInfo and optional AttachmentData */
  composeAttachment: (
    fileInfo: FileInfo,
    attachmentData?: AttachmentData,
  ): Attachment => {
    return {
      ...fileInfo,
      data: attachmentData,
    };
  },
  /** Composes an Attachment with FileInfo and optional AttachmentAWSData */
  composeAttachmentAWS: (
    fileInfo: FileInfo,
    attachmentData: AttachmentAWSData,
  ): AttachmentAWS => {
    return {
      ...fileInfo,
      data: attachmentData,
    };
  },

  /** Formats attachments for posting a new comment */
  forCommentPost: (attachments: AttachmentItems) =>
    AttachmentUtils.withCompleted(attachments).map(
      (attachment) =>
        attachment.data
          ? {
              uuid: attachment.data.uuid,
              url: attachment.data.url,
              filename: attachment.filename,
            }
          : {},
    ),

  /** Returns the extension of a filename string */
  getFileExtension: (filename: string) => {
    const lastIndex = filename.lastIndexOf('.') + 1;
    return lastIndex ? filename.substr(lastIndex).toLowerCase() : '';
  },

  /** Returns the icon name associated with the given extension */
  getFileIcon: (extension: string = '') =>
    fileIconMap[extension] || 'paperclip',

  /** Returns formatted files size from given number of bytes */
  getFileSize: (size: number) => {
    const convert = (limit: number) => (size / limit).toFixed(1);

    if (size < 0) {
      return 'invalid file size';
    }

    if (size >= 1048576) {
      return `${convert(1048576)} MB`;
    }

    if (size >= 1024) {
      return `${convert(1024)} KB`;
    }

    return `${size} bytes`;
  },

  /** Returns a boolean based on if items exists and done uploading */
  hasPendingAttachments: (attachments: AttachmentItems): boolean => {
    const items = AttachmentUtils.asArray(attachments);
    return items.length > 0 && !AttachmentUtils.isUploadingComplete(items);
  },

  /** Returns a boolean value based on if all attachments are complete */
  isUploadingComplete: (attachments: Attachment[]): boolean =>
    attachments.every(({ status = {} }) => status.complete!),

  /** Returns an Attachment API response's attachment data */
  pickAttachmentData: (response: AttachmentResponse): AttachmentData =>
    response.attachment_data,

  /** Validates and formats a File that will become an Attachment */
  processFile: (file: File): FileInfo => {
    const errors: Set<AttachmentErrors> = new Set();
    const extension = AttachmentUtils.getFileExtension(file.name);
    const icon = AttachmentUtils.getFileIcon(extension);

    if (file.size > MAX_FILE_SIZE) {
      errors.add(AttachmentErrors.INVALID_SIZE);
    }

    if (file.name.length > 100) {
      errors.add(AttachmentErrors.INVALID_NAME_LENGTH);
    }

    return {
      id: MiscUtils.uuid(),
      content: file,
      extension,
      filename: file.name,
      icon,
      isValid: errors.size === 0,
      errors,
    };
  },

  /** Returns an array of completed Attachments */
  withCompleted: (attachments: AttachmentItems) =>
    AttachmentUtils.asArray(attachments).filter(
      (attachment) => attachment.status && attachment.status.complete,
    ),

  /** Returns an array of Attachments with upload errors */
  withUploadErrors: (attachments: AttachmentItems) =>
    AttachmentUtils.asArray(attachments).filter(
      (attachment) =>
        attachment.errors &&
        (attachment.errors.has(AttachmentErrors.ATTACHMENT_DATA_FAILURE) ||
          attachment.errors.has(AttachmentErrors.UPLOAD_FAILURE)),
    ),
};

export { AttachmentErrors, AttachmentUtils };
